import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/endpoints';
import { parseSizeToBytes } from '@/features/files/types/file.types';
import axios from 'axios';
import type {
  DeclarationKind,
  DraftCopyStatus,
  TaxFilingDocument,
  TaxFilingDocumentApi,
  TaxFilingPreviewResponse,
  TaxFilingUploadUrlResponse,
} from '../types/taxFilings.types';
import {
  DECLARATION_KIND,
  DRAFT_COPY_STATUS,
} from '../types/taxFilings.types';

const VALID_DRAFT_STATUSES: DraftCopyStatus[] = Object.values(DRAFT_COPY_STATUS);
const VALID_KINDS: DeclarationKind[] = Object.values(DECLARATION_KIND);

function normalizeDraftStatus(
  raw: string | undefined
): DraftCopyStatus | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim().toUpperCase();
  if (VALID_DRAFT_STATUSES.includes(s as DraftCopyStatus)) {
    return s as DraftCopyStatus;
  }
  return undefined;
}

function normalizeKind(raw: string | undefined): DeclarationKind | undefined {
  if (!raw) return undefined;
  const s = String(raw).trim().toUpperCase();
  if (VALID_KINDS.includes(s as DeclarationKind)) {
    return s as DeclarationKind;
  }
  return undefined;
}

function mapDoc(doc: TaxFilingDocumentApi): TaxFilingDocument {
  return {
    id: doc._id ?? '',
    name: doc.name ?? 'Unknown',
    size: parseSizeToBytes(doc.size),
    mimeType: doc.type ?? 'application/octet-stream',
    createdAt: doc.createdAt,
    lastModifiedBy: doc.lastModifiedBy,
    status: normalizeDraftStatus(doc.status),
    rejectionComment: doc.rejectionComment,
    kind: normalizeKind(doc.kind),
  };
}

function toList(
  data: TaxFilingDocumentApi[] | { files?: TaxFilingDocumentApi[] } | TaxFilingDocumentApi
): TaxFilingDocumentApi[] {
  if (Array.isArray(data)) return data;
  if (
    data &&
    typeof data === 'object' &&
    'files' in data &&
    Array.isArray((data as { files?: TaxFilingDocumentApi[] }).files)
  ) {
    return (data as { files: TaxFilingDocumentApi[] }).files;
  }
  if (data && typeof data === 'object' && 'name' in data) {
    return [data as TaxFilingDocumentApi];
  }
  return [];
}

async function listFrom(
  endpoint: string,
  params: Record<string, string | number>
): Promise<TaxFilingDocument[]> {
  const response = await apiClient.get<
    TaxFilingDocumentApi[] | { files?: TaxFilingDocumentApi[] } | TaxFilingDocumentApi
  >(endpoint, { params });
  return toList(response.data).map(mapDoc);
}

async function previewFrom(endpoint: string): Promise<string> {
  const response = await apiClient.get<TaxFilingPreviewResponse>(endpoint);
  return response.data.url;
}

function formatSizeForApi(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)}MB`;
}

// ---- Tax Summaries ----

export async function getTaxSummaries(taxYear: number): Promise<TaxFilingDocument[]> {
  return listFrom(API_ENDPOINTS.TAX_SUMMARIES, { taxYear });
}

export async function getTaxSummaryPreviewUrl(documentId: string): Promise<string> {
  return previewFrom(API_ENDPOINTS.TAX_SUMMARY_BY_ID(documentId));
}

export async function updateTaxSummaryStatus(
  documentId: string,
  status:
    | typeof DRAFT_COPY_STATUS.CLIENT_VERIFIED
    | typeof DRAFT_COPY_STATUS.CLIENT_REJECTED,
  rejectionComment?: string
): Promise<void> {
  const body: { status: string; rejectionComment?: string } = { status };
  if (
    status === DRAFT_COPY_STATUS.CLIENT_REJECTED &&
    rejectionComment != null &&
    rejectionComment.trim()
  ) {
    body.rejectionComment = rejectionComment.trim();
  }
  await apiClient.put(API_ENDPOINTS.TAX_SUMMARY_BY_ID(documentId), body);
}

// ---- Draft Copies ----

export async function getDraftCopies(taxYear: number): Promise<TaxFilingDocument[]> {
  return listFrom(API_ENDPOINTS.DRAFT_COPIES, { taxYear });
}

export async function getDraftCopyPreviewUrl(documentId: string): Promise<string> {
  return previewFrom(API_ENDPOINTS.DRAFT_COPY_BY_ID(documentId));
}

export async function updateDraftCopyStatus(
  documentId: string,
  status:
    | typeof DRAFT_COPY_STATUS.CLIENT_VERIFIED
    | typeof DRAFT_COPY_STATUS.CLIENT_REJECTED,
  rejectionComment?: string
): Promise<void> {
  const body: { status: string; rejectionComment?: string } = { status };
  if (
    status === DRAFT_COPY_STATUS.CLIENT_REJECTED &&
    rejectionComment != null &&
    rejectionComment.trim()
  ) {
    body.rejectionComment = rejectionComment.trim();
  }
  await apiClient.put(API_ENDPOINTS.DRAFT_COPY_BY_ID(documentId), body);
}

// ---- Declarations ----

export async function getDeclarations(
  taxYear: number,
  kind?: DeclarationKind
): Promise<TaxFilingDocument[]> {
  return listFrom(API_ENDPOINTS.DECLARATIONS, {
    taxYear,
    ...(kind ? { kind } : {}),
  });
}

export async function getDeclarationPreviewUrl(documentId: string): Promise<string> {
  return previewFrom(API_ENDPOINTS.DECLARATION_BY_ID(documentId));
}

/** Upload signed declaration: POST metadata, S3 PUT, then confirm. */
export async function uploadSignedDeclaration(
  file: File,
  taxYear: number,
  onProgress?: (progress: number) => void
): Promise<{ documentId: string }> {
  const contentType = file.type || 'application/octet-stream';
  const response = await apiClient.post<TaxFilingUploadUrlResponse>(
    API_ENDPOINTS.DECLARATIONS,
    {
      taxYear,
      name: file.name,
      type: contentType,
      size: formatSizeForApi(file.size),
    }
  );

  const { uploadUrl, key, _id } = response.data;
  if (!uploadUrl) {
    throw new Error('No upload URL returned');
  }

  const documentId = _id ?? key?.split('/').pop() ?? '';

  try {
    await axios.put(uploadUrl, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (progressEvent) => {
        if (progressEvent.total && onProgress) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });

    if (documentId) {
      await apiClient.post(API_ENDPOINTS.DECLARATION_CONFIRM_UPLOAD(documentId));
    }

    return { documentId };
  } catch (err) {
    if (documentId) {
      try {
        await apiClient.post(API_ENDPOINTS.DECLARATION_FAIL_UPLOAD(documentId));
      } catch {
        // Best-effort cleanup
      }
    }
    throw err;
  }
}

// ---- Final Copies ----

export async function getFinalCopies(taxYear: number): Promise<TaxFilingDocument[]> {
  return listFrom(API_ENDPOINTS.FINAL_COPIES, { taxYear });
}

export async function getFinalCopyPreviewUrl(documentId: string): Promise<string> {
  return previewFrom(API_ENDPOINTS.FINAL_COPY_BY_ID(documentId));
}

export async function updateFinalCopyStatus(
  documentId: string,
  status:
    | typeof DRAFT_COPY_STATUS.CLIENT_VERIFIED
    | typeof DRAFT_COPY_STATUS.CLIENT_REJECTED,
  rejectionComment?: string
): Promise<void> {
  const body: { status: string; rejectionComment?: string } = { status };
  if (
    status === DRAFT_COPY_STATUS.CLIENT_REJECTED &&
    rejectionComment != null &&
    rejectionComment.trim()
  ) {
    body.rejectionComment = rejectionComment.trim();
  }
  await apiClient.put(API_ENDPOINTS.FINAL_COPY_BY_ID(documentId), body);
}
